import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { NavigationStart, Router } from '@angular/router';
import { AudioCallAttemptAcceptRequest, AvatarOrderDataResponse, AvatarOrderResponse, ChatDataResponse, ChatRequest, Client } from '@api/client';
import { ConnectAudioService } from '@services/connect-audio.service';
import { CHAT_CALL_STATUS } from '@chat/interfaces/chat.interfaces';
import { AuthService } from '@services/auth.service';
import { LoadingService } from '@services/loading.service';
import { ToastService } from '@services/toast.service';
import { WebSocketController } from '@services/web-socket.controller';
import { NavigationStore } from '@services/navigation.store';
import { EMPTY, filter, firstValueFrom, merge, Observable, pairwise, skip, Subject, Subscription, takeUntil, timeout, TimeoutError, timer } from 'rxjs';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { CallSessionService } from '@call/services/call-session.service';
import { CallOrderService } from '@call/services/call-order.service';
import { CallPermissionsService } from '@call/services/call-permissions.service';
import { CALL_ORDER_STATUS_NUMBERS, CALL_ORDER_TYPE_NUMBERS } from '@call/call.constants';
import { ActiveAudioCallUiService } from '@call/services/active-audio-call-ui.service';
import { ActiveAudioCallNativeService } from '@call/services/active-audio-call-native.service';
import type { EventAudioCallPartnerConnectedStatus } from '@services/audio-call.socket.interfaces';
import { AudioCallDeliveryStatus, AudioCallReliableMeta, SOCKET_EVENT_NAMES } from '@services/web-socket.interfaces';
import { SubscriptionsBag } from '@utils/subscriptions-bag';
import { AppNavigationService } from '@state-routing';
import { AudioCallConnectionDebugService } from '@services/audio-call-connection-debug.service';
import { CallOrderUpdatesService } from '@app/services/call-order-updates.service';
import { AudioCallFinalizationService } from '@app/services/audio-call-finalization.service';
import { createAudioCallAttemptId } from '@app/services/audio-call-attempt';
import { IncomingAudioPreparation } from '@call/interfaces/incoming-audio-preparation.interface';

export interface IncomingCallOpenResult {
  readonly opened: boolean;
  readonly navigationCommands: (string | number)[] | null;
  readonly orderId: number | null;
  readonly shouldAcceptAudioInBackground: boolean;
  readonly audioOrderResponse: AvatarOrderDataResponse | null;
}

interface ReliableAudioCallProcessingResult {
  status: AudioCallDeliveryStatus;
  errorMessage?: string;
  error?: unknown;
}

interface MinimizedAudioCallStopOptions {
  sendToPartner: boolean;
}

interface IncomingAudioAcceptOperation {
  cancel: (finalize?: boolean) => void;
  isCurrent: () => boolean;
  result: Promise<void>;
}

@Injectable({
  providedIn: 'root'
})
export class AudioCallService implements OnDestroy {
  private readonly connectionDebug = inject(AudioCallConnectionDebugService);
  private readonly orderUpdates = inject(CallOrderUpdatesService);
  private readonly finalization = inject(AudioCallFinalizationService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly callSessionService: CallSessionService = inject(CallSessionService);
  private readonly connectAudioService: ConnectAudioService = inject(ConnectAudioService);
  private readonly callOrderService: CallOrderService = inject(CallOrderService);
  private readonly activeAudioCallNativeService: ActiveAudioCallNativeService = inject(ActiveAudioCallNativeService);
  private readonly activeAudioCallUiService: ActiveAudioCallUiService = inject(ActiveAudioCallUiService);
  private readonly loadingService: LoadingService = inject(LoadingService);
  private readonly callPermissionsService: CallPermissionsService = inject(CallPermissionsService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly webSocketController: WebSocketController = inject(WebSocketController);
  private readonly navigationStore: NavigationStore = inject(NavigationStore);
  private readonly appNavigation: AppNavigationService = inject(AppNavigationService);
  private readonly router: Router = inject(Router);
  private readonly client: Client = inject(Client);
  private readonly datePipe: DatePipe = inject(DatePipe);
  private readonly translate: TranslateService = inject(TranslateService);
  public readonly incomingAcceptFailed$ = new Subject<number>();
  public readonly initialConnectionTimedOut$ = new Subject<number>();
  private readonly initialConnectionTimeoutMs: number = 30000;
  private initialConnectionWatch: {identity: object; orderId: number; cancel: () => void} | null = null;
  private readonly incomingCallOperations = new Map<number, {
    userId: number; sessionVersion: number; promise: Promise<IncomingCallOpenResult>;
  }>();
  private incomingAudioPreparation: {identity: object | null; createdSession: boolean} | null = null;
  private readonly incomingCallAcceptOperations = new Map<number, IncomingAudioAcceptOperation>();
  private readonly incomingSocketInitTimeoutMs: number = 8000;
  private readonly outgoingSocketInitTimeoutMs: number = 8000;
  private readonly incomingAcceptDeadlineMs: number = 12000;
  private readonly incomingAcceptResponseTimeoutMs: number = 5000;
  private readonly incomingAcceptRetryCount: number = 2;
  private readonly incomingAcceptRetryDelayMs: number = 250;
  private readonly minimizedCustomerReconnectTimeoutMs: number = 30000;
  private readonly processedReliableMessageTtlMs: number = 120000;
  private readonly sb: SubscriptionsBag = new SubscriptionsBag();
  private minimizedCustomerReconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private processingReliableMessages: Map<string, Promise<ReliableAudioCallProcessingResult>> = new Map();
  private processedMinimizedExitMessages: Map<string, number> = new Map();
  private processedMinimizedPartnerConnectedStatusMessages: Map<string, number> = new Map();
  private isHandlingMinimizedReconnectFailure: boolean = false;
  private outgoingCallStartPending: boolean = false;
  private cancelOutgoingCallStart: (() => void) | null = null;
  private readonly orderReconciliations: Map<number, Promise<void>> = new Map();

  constructor() {
    this.subscribeToMinimizedCallEvents();
    this.subscribeToMinimizedReconnectEvents();
    this.subscribeToCallOrderReconciliation();
  }

  ngOnDestroy(): void {
    this.initialConnectionWatch?.cancel();
    this.cancelOutgoingCallStart?.();
    this.incomingCallAcceptOperations.forEach(operation => operation.cancel());
    this.clearMinimizedCustomerReconnectTimeout();
    this.processingReliableMessages.clear();
    this.processedMinimizedExitMessages.clear();
    this.processedMinimizedPartnerConnectedStatusMessages.clear();
    this.sb.unsubscribeAll();
  }

  public async openIncomingCall(orderId: number): Promise<IncomingCallOpenResult> {
    const userId = this.authService.parsedToken?.userId;
    const sessionVersion = this.authService.sessionVersion;
    if (!userId) return this.getIncomingCallFailedResult();
    const isCurrent = (): boolean => this.authService.parsedToken?.userId === userId
      && this.authService.sessionVersion === sessionVersion;
    const normalizedOrderId: number | null = this.normalizeOrderId(orderId);
    if (normalizedOrderId === null) {
      console.error('Order id is not defined!');
      await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
      return this.getIncomingCallFailedResult();
    }

    const existingOperation = this.incomingCallOperations.get(normalizedOrderId);
    if (existingOperation?.userId === userId && existingOperation.sessionVersion === sessionVersion) {
      return await existingOperation.promise;
    }

    if (!this.hasActiveAudioCall()) {
      this.connectionDebug.begin('executor', normalizedOrderId);
      this.connectionDebug.milestone('ui.incoming_opened');
    }

    const operation: Promise<IncomingCallOpenResult> = this.openIncomingCallInternal(normalizedOrderId, isCurrent)
      .finally((): void => {
        if (this.incomingCallOperations.get(normalizedOrderId)?.promise === operation) {
          this.incomingCallOperations.delete(normalizedOrderId);
        }
      });

    this.incomingCallOperations.set(normalizedOrderId, {userId, sessionVersion, promise: operation});
    return await operation;
  }

  private async openIncomingCallInternal(orderId: number, isCurrent: () => boolean): Promise<IncomingCallOpenResult> {
    try {
      const response: AvatarOrderDataResponse = await this.callOrderService.getOrder(orderId, false);
      if (!isCurrent()) return this.getIncomingCallFailedResult();
      const order: AvatarOrderResponse = response?.data;
      if (!order) {
        console.error('Order data is not defined!');
        await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
        return this.getIncomingCallFailedResult();
      }
      if (order.to?.id !== this.authService.parsedToken?.userId) {
        return this.getIncomingCallFailedResult();
      }

      const isAudioCall: boolean = this.callOrderService.isAudioCallOrder(order);
      if (!isAudioCall) {
        console.error('Incoming call route received non-audio order type');
        await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
        return this.getIncomingCallFailedResult();
      }

      return await this.prepareIncomingAudioCall(orderId, response);
    } catch (error) {
      if (!isCurrent()) return this.getIncomingCallFailedResult();
      console.error(error);
      await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
      return this.getIncomingCallFailedResult();
    }
  }

  private async prepareIncomingAudioCall(orderId: number, response?: AvatarOrderDataResponse): Promise<IncomingCallOpenResult> {
    try {
      const orderResponse: AvatarOrderDataResponse = response ?? await this.callOrderService.getOrder(orderId);

      const order: AvatarOrderResponse = orderResponse?.data;
      if (!order) {
        console.error('Order data is not defined!');
        await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
        return this.getIncomingCallFailedResult();
      }
      if (!this.callOrderService.isValidOrder(order)) {
        if (this.callOrderService.isOrderFinal(order.status)) {
          console.warn('Order status: ', order.status);
        } else {
          console.error('Order is not valid!');
        }
        await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
        return this.getIncomingCallFailedResult();
      }
      const isAudioCall: boolean = this.callOrderService.isAudioCallOrder(order);
      if (!isAudioCall) {
        console.error('Order type is not audio call!');
        await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
        return this.getIncomingCallFailedResult();
      }

      return {
        opened: true,
        navigationCommands: ['/audio-call'],
        orderId,
        shouldAcceptAudioInBackground: true,
        audioOrderResponse: orderResponse,
      };
    } catch (error) {
      console.error(error);
      await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
      return this.getIncomingCallFailedResult();
    }
  }

  public setupIncomingAudioCall(orderResponse: AvatarOrderDataResponse): IncomingAudioPreparation {
    const previousIdentity = this.callSessionService.mediaSessionIdentity;
    const previousPreparation = this.incomingAudioPreparation;
    const owner = {identity: previousIdentity, createdSession: false};
    this.incomingAudioPreparation = owner;
    const userId = this.authService.parsedToken?.userId;
    const sessionVersion = this.authService.sessionVersion;
    const ownsPreparation = (): boolean => this.incomingAudioPreparation === owner && owner.identity !== null
      && this.callSessionService.mediaSessionIdentity === owner.identity
      && this.callSessionService.selectedOrder?.id === orderResponse.data?.id;
    const preparation: IncomingAudioPreparation = {
      isCurrent: (): boolean => ownsPreparation() && !this.callSessionService.isMediaSessionEnding
        && this.authService.parsedToken?.userId === userId && this.authService.sessionVersion === sessionVersion,
      commit: (): boolean => {
        if (!preparation.isCurrent()) return false;
        owner.createdSession = false;
        return true;
      },
      rollback: (): void => {
        // An older route must not clear a newer preparation, even for the same order.
        if (!ownsPreparation() || !owner.createdSession || this.callSessionService.isMediaSessionEnding
          || this.hasStartedActiveAudioCall()) return;
        this.incomingAudioPreparation = null;
        this.callSessionService.clearSelectedOrder();
      },
    };
    try {
      this.cancelOutgoingCallStart?.();
      this.activeAudioCallUiService.expand();
      try {
        this.callSessionService.setSelectedOrder(orderResponse, false);
      } finally {
        owner.identity = this.callSessionService.mediaSessionIdentity;
        owner.createdSession = owner.identity !== previousIdentity
          || (previousPreparation?.identity === owner.identity && previousPreparation.createdSession);
      }
      this.connectAudioService.subscribeToEvents();
      return preparation;
    } catch (error) {
      preparation.rollback();
      throw error;
    }
  }

  public finalizeIncomingAudioCall(orderId: number): void {
    this.scheduleIncomingAudioAccept(orderId);
  }

  public cancelIncomingAudioCall(orderId: number | null | undefined, finalize: boolean = true): void {
    if (this.initialConnectionWatch && this.initialConnectionWatch.orderId === orderId) this.initialConnectionWatch.cancel();
    if (orderId) this.incomingCallAcceptOperations.get(orderId)?.cancel(finalize);
  }

  public watchInitialConnection(orderId: number): boolean {
    const order = this.callSessionService.selectedOrder;
    const identity = this.callSessionService.mediaSessionIdentity;
    const userId = this.authService.parsedToken?.userId;
    const authVersion = this.authService.sessionVersion;
    if (!identity || !userId || order?.id !== orderId || order.myUserId !== userId
      || order.type !== CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL || this.callSessionService.isMediaSessionEnding) return false;
    if (this.callSessionService.hasEstablishedMedia(orderId)) return true;
    if (this.initialConnectionWatch?.identity === identity) return true;
    this.initialConnectionWatch?.cancel();
    const subscriptions = new Subscription();
    const isCurrent = (): boolean => this.callSessionService.mediaSessionIdentity === identity
      && this.authService.parsedToken?.userId === userId && this.authService.sessionVersion === authVersion
      && !this.callSessionService.isMediaSessionEnding;
    const cancel = (): void => {
      clearTimeout(deadline);
      subscriptions.unsubscribe();
      if (this.initialConnectionWatch?.identity === identity) this.initialConnectionWatch = null;
    };
    const deadline = setTimeout((): void => {
      cancel();
      if (!isCurrent() || this.callSessionService.hasEstablishedMedia(orderId)) return;
      this.callSessionService.markMediaSessionEnding(identity);
      this.connectionDebug.milestone('ui.initial_connection_timeout', {timeoutMs: this.initialConnectionTimeoutMs}, 'error');
      // Stop the peer and capture the fenced finish before any UI/native await.
      const stopped = order.isCustomer ? this.connectAudioService.stopWatchingByCustomer(true)
        : this.connectAudioService.stopSharingForExecutor(true);
      this.initialConnectionTimedOut$.next(orderId);
      void stopped.catch((): void => {
        if (this.callSessionService.mediaSessionIdentity === identity) {
          this.connectionDebug.milestone('ui.initial_connection_cleanup_failed', undefined, 'error');
        }
      });
    }, this.initialConnectionTimeoutMs);
    this.initialConnectionWatch = {identity, orderId, cancel};
    const check = (): void => {
      if (!isCurrent() || this.callSessionService.hasEstablishedMedia(orderId)) cancel();
    };
    subscriptions.add(this.authService.parsedToken$.subscribe(check));
    subscriptions.add(this.callSessionService.selectedOrderChanged$.subscribe(check));
    subscriptions.add(this.connectAudioService.webrtcAudioService.isConnected$.subscribe(check));
    return true;
  }

  public hasActiveAudioCall(): boolean {
    const currentOrderId: number | null = this.normalizeOrderId(this.callSessionService.selectedOrder?.id);
    if (currentOrderId === null) return false;
    return this.callSessionService.selectedOrder?.type === CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL;
  }

  public hasStartedActiveAudioCall(): boolean {
    if (!this.hasActiveAudioCall()) return false;
    return this.connectAudioService.inStream
      || !!this.connectAudioService.webrtcAudioService.peerConnection
      || this.connectAudioService.webrtcAudioService.streamStartTime > 0;
  }

  public async minimizeCurrentAudioCallToChat(): Promise<boolean> {
    if (!this.hasStartedActiveAudioCall()) return false;
    const identity = this.callSessionService.mediaSessionIdentity;
    const isCurrent = (): boolean => this.callSessionService.mediaSessionIdentity === identity;

    const partnerId: number | null = this.normalizeOrderId(this.callSessionService.selectedOrder?.partnerId);
    if (partnerId === null) {
      await this.toastService.error(this.translate.instant('call.toasts.userNotDefined'));
      return false;
    }

    let chatId: number | null = null;
    try {
      chatId = await this.resolveChatIdForPartner(partnerId);
    } catch (error) {
      if (!isCurrent()) return false;
      console.error('Failed to resolve active audio call chat', error);
      await this.toastService.error(this.translate.instant('avatar.toasts.getChatError'));
      return false;
    }
    if (!isCurrent()) return false;
    if (chatId === null) {
      await this.toastService.error(this.translate.instant('avatar.toasts.getChatError'));
      return false;
    }

    this.activeAudioCallUiService.minimize();
    await this.activeAudioCallNativeService.syncForCurrentCall();
    if (!isCurrent()) return false;
    const releaseNavigation = this.callSessionService.allowNavigationFromCall();

    try {
      const navigated = await this.appNavigation.navigateForward(['/chats', chatId], {replaceUrl: true}, isCurrent);
      if (!isCurrent()) return false;
      if (!navigated) {
        this.activeAudioCallUiService.expand();
        await this.activeAudioCallNativeService.deactivateForCurrentCall();
        return false;
      }
      this.navigationStore.setBackUrl(`/chats/${chatId}`);
      return true;
    } catch (error) {
      if (!isCurrent()) return false;
      this.activeAudioCallUiService.expand();
      await this.activeAudioCallNativeService.deactivateForCurrentCall();
      if (!isCurrent()) return false;
      console.error('Failed to navigate to active audio call chat', error);
      await this.toastService.error(this.translate.instant('avatar.toasts.getChatError'));
      return false;
    } finally {
      releaseNavigation();
    }
  }

  public async openCurrentAudioCallScreen(): Promise<void> {
    if (!this.hasActiveAudioCall()) {
      this.activeAudioCallUiService.clear();
      await this.activeAudioCallNativeService.deactivateForCurrentCall({finalizeAudioSession: true});
      return;
    }

    const identity = this.callSessionService.mediaSessionIdentity;
    const isCurrent = (): boolean => this.callSessionService.mediaSessionIdentity === identity;
    const wasMinimized: boolean = this.activeAudioCallUiService.isMinimized;
    this.activeAudioCallUiService.expand();
    await this.activeAudioCallNativeService.deactivateForCurrentCall();
    if (!isCurrent()) return;
    if (this.callSessionService.isMediaSessionEnding) {
      await this.stopCurrentAudioCall({sendToPartner: false});
      return;
    }

    let navigated = false;
    try {
      navigated = await this.appNavigation.navigateForward(['/audio-call'], {},
        () => isCurrent() && !this.callSessionService.isMediaSessionEnding);
    } catch (error) {
      console.error('Failed to open active audio call screen', error);
    } finally {
      // A successful route owns its screen teardown; an aborted route has no consumer.
      if (!navigated && isCurrent() && this.callSessionService.isMediaSessionEnding) {
        await this.stopCurrentAudioCall({sendToPartner: false});
      } else if (!navigated && isCurrent() && wasMinimized) {
        this.activeAudioCallUiService.minimize();
        await this.activeAudioCallNativeService.syncForCurrentCall();
      }
    }
  }

  public isAudioCallScreenForOrder(url: string, orderId: number): boolean {
    const normalizedOrderId: number | null = this.normalizeOrderId(orderId);
    if (normalizedOrderId === null) return false;

    const currentOrderId: number | null = this.normalizeOrderId(this.callSessionService.selectedOrder?.id);
    if (currentOrderId !== normalizedOrderId) return false;

    const currentPath: string = this.getPathFromUrl(url);
    return currentPath.includes('/audio-call');
  }

  public isIncomingCallScreenForOrder(url: string, orderId: number): boolean {
    const normalizedOrderId: number | null = this.normalizeOrderId(orderId);
    if (normalizedOrderId === null) return false;

    const currentPath: string = this.getPathFromUrl(url);
    return currentPath === `/incoming-call/${normalizedOrderId}`;
  }

  public async startOutgoingAudioCallFromUi(
    toUserId: number | null | undefined,
    options: { backUrl?: string | null; isGroupChat?: boolean } = {},
  ): Promise<boolean> {
    if (this.outgoingCallStartPending) return false;
    const backUrl: string | null = options.backUrl ?? null;
    const isGroupChat: boolean = options.isGroupChat ?? false;

    if (isGroupChat) {
      await this.toastService.error(this.translate.instant('chat.call.error'));
      return false;
    }

    const myUserId: number | undefined = this.authService.parsedToken?.userId;
    if (!toUserId || !myUserId || toUserId === myUserId) {
      await this.toastService.error(this.translate.instant('call.toasts.userNotDefined'));
      return false;
    }

    this.outgoingCallStartPending = true;
    const cancelSignal = new Subject<void>();
    const subscriptions = new Subscription();
    const selectedOrder = this.callSessionService.selectedOrder;
    let cancelled: boolean = false;
    let committed: boolean = false;
    let loadingStarted: boolean = false;
    let diagnosticSessionId: string | null = null;
    const cancel = (): void => {
      if (committed || cancelled) return;
      cancelled = true;
      cancelSignal.next();
    };
    const isCurrent = (): boolean => !cancelled
      && this.authService.parsedToken?.userId === myUserId
      && this.callSessionService.selectedOrder === selectedOrder;
    this.cancelOutgoingCallStart = cancel;
    try {
      subscriptions.add(this.authService.parsedToken$.subscribe(token => {
        if (token?.userId !== myUserId) cancel();
      }));
      subscriptions.add(this.router.events.subscribe(event => {
        if (event instanceof NavigationStart) cancel();
      }));
      if (!this.hasActiveAudioCall()) {
        diagnosticSessionId = this.connectionDebug.begin('customer');
        this.connectionDebug.milestone('ui.call_pressed');
      }
      if (!isCurrent()) {
        this.connectionDebug.finishBeforeOrder(diagnosticSessionId, 'ui.start_cancelled');
        return false;
      }
      if (!this.webSocketController.socketConnected) {
        this.connectionDebug.finishBeforeOrder(diagnosticSessionId, 'ui.socket_unavailable');
        await this.toastService.error(this.translate.instant('call.toasts.socketNotConnected'));
        return false;
      }

      const socketReady: boolean = await this.waitForOutgoingSocketInit(cancelSignal);
      if (!isCurrent()) {
        this.connectionDebug.finishBeforeOrder(diagnosticSessionId, 'ui.start_cancelled');
        return false;
      }
      if (!socketReady) {
        this.connectionDebug.finishBeforeOrder(diagnosticSessionId, 'ui.socket_init_timeout');
        await this.toastService.error(this.translate.instant('call.toasts.socketNotConnected'));
        return false;
      }
      const hasMicrophoneAccess: boolean = await this.ensureMicrophonePermission();
      if (!isCurrent()) {
        this.connectionDebug.finishBeforeOrder(diagnosticSessionId, 'ui.start_cancelled');
        return false;
      }
      if (!hasMicrophoneAccess) {
        this.connectionDebug.finishBeforeOrder(diagnosticSessionId, 'ui.microphone_denied');
        return false;
      }

      loadingStarted = true;
      await this.loadingService.start();
      if (!isCurrent()) {
        this.connectionDebug.finishBeforeOrder(diagnosticSessionId, 'ui.start_cancelled');
        return false;
      }
      if (!this.webSocketController.socketConnected || !this.webSocketController.isSocketInitedSubject.value) {
        this.connectionDebug.finishBeforeOrder(diagnosticSessionId, 'ui.socket_unavailable');
        await this.toastService.error(this.translate.instant('call.toasts.socketNotConnected'));
        return false;
      }
      const started: boolean = await this.startOutgoingAudioCall(toUserId, backUrl, isCurrent, (): void => { committed = true; }, cancelSignal);
      if (!started) this.connectionDebug.finishBeforeOrder(diagnosticSessionId, 'ui.start_cancelled');
      return started;
    } catch (error) {
      if (cancelled) {
        this.connectionDebug.finishBeforeOrder(diagnosticSessionId, 'ui.start_cancelled');
        return false;
      }
      this.connectionDebug.finishBeforeOrder(diagnosticSessionId, 'ui.order_create_failed');
      if (error instanceof Error
        && (error.message === 'No order id in response' || error.message === 'No date from in request')) {
        console.error(error);
        await this.toastService.error(this.translate.instant('call.toasts.createOrderError'));
        return false;
      }

      const { status, message } = this.getCallErrorInfo(error);
      const isCallFailError: boolean = status === HttpStatusCode.Forbidden && message === CHAT_CALL_STATUS.FAIL;
      const messageKey: string = isCallFailError ? 'chat.call.fail' : 'chat.call.error';
      if (!isCallFailError) console.error(error);
      await this.toastService.error(this.translate.instant(messageKey));
      return false;
    } finally {
      subscriptions.unsubscribe();
      cancelSignal.complete();
      this.cancelOutgoingCallStart = null;
      try {
        if (loadingStarted) await this.loadingService.stop();
      } finally {
        this.outgoingCallStartPending = false;
      }
    }
  }

  private async startOutgoingAudioCall(
    toUserId: number,
    backUrl: string | null,
    isCurrent: () => boolean,
    commit: () => void,
    cancel$: Observable<void> = EMPTY,
  ): Promise<boolean> {
    const dateFromAt: string | null = this.datePipe.transform(Date.now(), 'yyyy-MM-dd HH:mm:ss');
    if (!dateFromAt) {
      throw new Error('No date from in request');
    }
    const userId = this.authService.parsedToken?.userId;
    if (!userId) throw new Error('Call account is unavailable');
    const attemptId = createAudioCallAttemptId();
    const response = await this.callOrderService.createOutgoingAudioCallOrder(toUserId, dateFromAt, attemptId, userId, cancel$);
    if (!response?.data?.id) throw new Error('No order id in response');
    // The POST may finish after navigation/account change. Do not overwrite another call.
    // Only our direct outgoing attempt can be cleaned up here, not a discovered reverse call.
    if (!isCurrent()) {
      if (response.data.type === CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL
        && response.data.from_id === userId && response.data.to_id === toUserId) {
        void this.finalization.finish(response.data.id, false, userId, attemptId);
      }
      return false;
    }

    const ownsCreatedOrder = response.data.type === CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL
      && response.data.from_id === userId && response.data.to_id === toUserId;
    const orderId = response.data.id;
    let selectedIdentity: object | null = null;
    let selectedAttemptId: string | null = null;
    let started = false;
    try {
      if (!this.callSessionService.setSelectedOrderForUser(response, userId)) {
        throw new Error('Current user is not a call participant');
      }
      selectedIdentity = this.callSessionService.mediaSessionIdentity;
      const isCustomer = this.callSessionService.selectedOrder?.isCustomer === true;
      if (isCustomer) {
        if (!ownsCreatedOrder || !this.callSessionService.setAttempt(orderId, userId, attemptId)) {
          throw new Error('Call attempt identity conflict');
        }
        selectedAttemptId = attemptId;
      }
      commit();
      this.connectionDebug.setOrderContext(orderId, isCustomer ? 'customer' : 'executor');
      this.connectionDebug.milestone('ui.order_received');

      this.activeAudioCallUiService.expand();
      this.connectAudioService.subscribeToEvents();
      const resolvedBackUrl: string = backUrl || this.router.url;
      this.navigationStore.setBackUrl(resolvedBackUrl);
      const canNavigate = (): boolean => this.authService.parsedToken?.userId === userId
        && this.isCurrentCallStart(orderId, userId, selectedIdentity, selectedAttemptId);
      const navigated = await this.appNavigation.navigateForward(['/audio-call'], {replaceUrl: true}, canNavigate);
      if (!navigated || !canNavigate()) return false;

      if (!isCustomer) this.finalizeIncomingAudioCall(orderId);
      started = true;
      return true;
    } finally {
      if (!started) {
        try {
          await this.releaseFailedCallStart(orderId, userId, selectedIdentity, selectedAttemptId);
        } finally {
          // A failed route never owns a discovered reverse call. Do not reject it.
          if (ownsCreatedOrder) void this.finalization.finish(orderId, false, userId, attemptId);
        }
      }
    }
  }

  private isCurrentCallStart(orderId: number, userId: number, identity: object | null, attemptId: string | null): boolean {
    return identity !== null && this.callSessionService.mediaSessionIdentity === identity
      && this.callSessionService.selectedOrder?.id === orderId
      && this.callSessionService.selectedOrder.myUserId === userId
      && this.callSessionService.getAttempt(orderId, userId) === attemptId;
  }

  private async releaseFailedCallStart(orderId: number, userId: number, identity: object | null, attemptId: string | null): Promise<void> {
    if (!this.isCurrentCallStart(orderId, userId, identity, attemptId)) return;
    this.connectionDebug.milestone('ui.call_screen_failed', undefined, 'error');
    this.clearMinimizedCustomerReconnectTimeout();
    this.connectAudioService.clearTimer();
    const stopped = this.callSessionService.selectedOrder?.isCustomer
      ? this.connectAudioService.stopWatching() : this.connectAudioService.stopSharing();
    // These stop methods release their media synchronously. Clear our selection
    // before yielding; no late continuation may clear a replacement call.
    this.callSessionService.clearSelectedOrder();
    this.activeAudioCallUiService.clear();
    this.navigationStore.clearBackUrl();
    this.connectionDebug.deactivate();
    await stopped;
  }

  private subscribeToCallOrderReconciliation(): void {
    this.sb.sub = this.webSocketController.isSocketInitedSubject.pipe(skip(1), filter(Boolean)).subscribe((): void => {
      void this.reconcileActiveAudioCall();
    });
    this.sb.sub = this.connectAudioService.webrtcAudioService.isConnected$.pipe(pairwise()).subscribe(([wasConnected, connected]): void => {
      if (wasConnected && !connected) void this.reconcileActiveAudioCall();
    });
    this.sb.sub = this.orderUpdates.orderUpdated$.subscribe((order): void => {
      if (this.activeAudioCallUiService.isMinimized && this.callOrderService.isOrderFinal(order.status)) {
        void this.handleMinimizedAudioCallExit(order.id, true).catch((error: unknown): void => {
          console.error('Failed to close finalized minimized call', error);
        });
      }
    });
  }

  private async reconcileActiveAudioCall(): Promise<void> {
    const orderId = this.callSessionService.selectedOrder?.id;
    const userId = this.authService.parsedToken?.userId;
    if (!orderId || !userId || !this.hasStartedActiveAudioCall()) return;
    const pending = this.orderReconciliations.get(orderId);
    if (pending) return pending;
    const task = this.readActiveAudioCallStatus(orderId, userId);
    this.orderReconciliations.set(orderId, task);
    try {
      await task;
    } finally {
      if (this.orderReconciliations.get(orderId) === task) this.orderReconciliations.delete(orderId);
    }
  }

  private async readActiveAudioCallStatus(orderId: number, userId: number): Promise<void> {
    try {
      const response = await firstValueFrom(this.client.avatarOrderGet(orderId).pipe(timeout(5000)));
      if (this.authService.parsedToken?.userId !== userId || this.callSessionService.selectedOrder?.id !== orderId) return;
      const order = response?.data;
      if (order?.id !== orderId || order.type !== CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL
        || (order.from_id !== userId && order.to_id !== userId)) return;
      if (!this.callOrderService.isOrderFinal(order.status)) return;
      this.connectionDebug.milestone('order.remote_finalized');
      this.orderUpdates.updateOrder(order);
    } catch {
      // An unavailable status endpoint is not proof that a live call has ended.
      if (this.authService.parsedToken?.userId === userId && this.callSessionService.selectedOrder?.id === orderId) {
        this.connectionDebug.milestone('order.reconcile_failed', undefined, 'warning');
      }
    }
  }

  private subscribeToMinimizedCallEvents(): void {
    this.sb.sub = this.webSocketController.audioCallEvent.subscribe(async (res: any): Promise<void> => {
      if (!this.shouldHandleMinimizedAudioCallEvent(res)) return;

      switch (res?.type) {
        case SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT:
          await this.handleMinimizedAudioCallExitEvent(res);
          break;
        case SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT_FROM_BACKEND:
          await this.handleMinimizedAudioCallExitFromBackendEvent(res);
          break;
        case SOCKET_EVENT_NAMES.AUDIO_CALL_PARTNER_CONNECTED_STATUS:
          await this.handleMinimizedAudioCallPartnerConnectedStatusEvent(res);
          break;
      }
    });
  }

  private subscribeToMinimizedReconnectEvents(): void {
    this.sb.sub = merge(this.incomingAcceptFailed$, this.initialConnectionTimedOut$).subscribe((orderId: number): void => {
      if (this.activeAudioCallUiService.isMinimized && this.callSessionService.selectedOrder?.id === orderId) {
        void this.stopCurrentAudioCall({sendToPartner: false}).catch((error: unknown): void => {
          console.error('Failed to close minimized call after accept failure', error);
        });
      }
    });
    this.sb.sub = this.activeAudioCallUiService.minimized$.subscribe((isMinimized: boolean): void => {
      if (isMinimized) {
        this.scheduleMinimizedReconnectTimeoutIfNeeded();
        return;
      }

      this.clearMinimizedCustomerReconnectTimeout();
    });

    this.sb.sub = this.connectAudioService.webrtcAudioService.isConnected$.subscribe((isConnected: boolean): void => {
      if (isConnected) {
        this.clearMinimizedCustomerReconnectTimeout();
        return;
      }

      this.scheduleMinimizedReconnectTimeoutIfNeeded();
    });

    this.sb.sub = merge(
      this.connectAudioService.webrtcAudioService.reconnectFailed$,
      this.connectAudioService.startupFailed$,
    ).subscribe((): void => {
      if (!this.activeAudioCallUiService.isMinimized) return;

      void this.handleMinimizedReconnectFailed('reconnect_failed').catch((error: unknown): void => {
        console.error('Failed to stop minimized audio call after reconnect failure', error);
      });
    });
  }

  private shouldHandleMinimizedAudioCallEvent(res: any): boolean {
    if (this.activeAudioCallUiService.isMinimized) return true;

    return this.isProcessedMinimizedReliableEvent(res);
  }

  private isProcessedMinimizedReliableEvent(res: any): boolean {
    const meta: AudioCallReliableMeta | null = res?.meta ?? null;
    const messageId: string | null = this.normalizeReliableMessageId(meta);
    if (!messageId) return false;
    const processingKey: string | null = this.buildReliableProcessingKey(meta);
    if (processingKey && this.getReliableMessageInProcessing(processingKey)) return true;

    switch (res?.type) {
      case SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT:
      case SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT_FROM_BACKEND:
        return this.isProcessedReliableMessage(this.processedMinimizedExitMessages, messageId);
      case SOCKET_EVENT_NAMES.AUDIO_CALL_PARTNER_CONNECTED_STATUS:
        return this.isProcessedReliableMessage(this.processedMinimizedPartnerConnectedStatusMessages, messageId);
      default:
        return false;
    }
  }

  private async handleMinimizedAudioCallExitEvent(res: any): Promise<void> {
    const meta: AudioCallReliableMeta | null = res?.meta ?? null;
    try {
      await this.processReliableMinimizedAudioCallEvent(
        meta,
        this.processedMinimizedExitMessages,
        async (): Promise<AudioCallDeliveryStatus> =>
          await this.handleMinimizedAudioCallExit(res?.data?.order_id, false),
      );
    } catch (error) {
      console.error('Failed to process minimized audio call exit event', error);
    }
  }

  private async handleMinimizedAudioCallExitFromBackendEvent(res: any): Promise<void> {
    const meta: AudioCallReliableMeta | null = res?.meta ?? null;
    try {
      await this.processReliableMinimizedAudioCallEvent(
        meta,
        this.processedMinimizedExitMessages,
        async (): Promise<AudioCallDeliveryStatus> =>
          await this.handleMinimizedAudioCallExit(res?.data?.order_id, true, res?.data?.reason),
      );
    } catch (error) {
      console.error('Failed to process minimized backend audio call exit event', error);
    }
  }

  private async handleMinimizedAudioCallPartnerConnectedStatusEvent(res: any): Promise<void> {
    const meta: AudioCallReliableMeta | null = res?.meta ?? null;
    try {
      await this.processReliableMinimizedAudioCallEvent(
        meta,
        this.processedMinimizedPartnerConnectedStatusMessages,
        async (): Promise<AudioCallDeliveryStatus> => {
          const statusData: EventAudioCallPartnerConnectedStatus | null = res?.data ?? null;
          return await this.handleMinimizedPartnerConnectedStatus(statusData);
        },
      );
    } catch (error) {
      console.error('Failed to process minimized partner connected status event', error);
    }
  }

  private async handleMinimizedAudioCallExit(
    orderId: number | null | undefined,
    fromBackend: boolean,
    reason?: string,
  ): Promise<AudioCallDeliveryStatus> {
    const currentOrderId: number | null = this.normalizeOrderId(this.callSessionService.selectedOrder?.id);
    const eventOrderId: number | null = this.normalizeOrderId(orderId);
    if (currentOrderId === null || eventOrderId !== currentOrderId) return 'ignored';

    await this.stopCurrentAudioCall({
      sendToPartner: false,
    });

    if (fromBackend && reason === 'Time of call has expired') {
      await this.toastService.warning(this.translate.instant('call.video_sharing.toasts.streamTimeout'));
    }

    return 'processed';
  }

  private async handleMinimizedPartnerConnectedStatus(data: EventAudioCallPartnerConnectedStatus | null): Promise<AudioCallDeliveryStatus> {
    if (!data) return 'ignored';

    const eventOrderId: number | null = this.normalizeOrderId(data.order_id);
    const currentOrderId: number | null = this.normalizeOrderId(this.callSessionService.selectedOrder?.id);
    if (eventOrderId !== null && eventOrderId !== currentOrderId) return 'ignored';

    if (data.reason === 'reconnect_rejected') {
      this.connectAudioService.webrtcAudioService.stopReconnectAttempts();
      const handled: boolean = await this.handleMinimizedReconnectFailed('reconnect_rejected');
      return handled ? 'processed' : 'ignored';
    }

    console.warn(data.message);
    return 'processed';
  }

  private scheduleMinimizedReconnectTimeoutIfNeeded(): void {
    if (!this.shouldWaitForMinimizedCustomerConnection()) {
      this.clearMinimizedCustomerReconnectTimeout();
      return;
    }

    if (this.minimizedCustomerReconnectTimeout) return;

    this.minimizedCustomerReconnectTimeout = setTimeout((): void => {
      this.minimizedCustomerReconnectTimeout = null;
      if (!this.shouldWaitForMinimizedCustomerConnection()) return;

      void this.handleMinimizedReconnectFailed('timeout').catch((error: unknown): void => {
        console.error('Failed to stop minimized audio call after connection timeout', error);
      });
    }, this.minimizedCustomerReconnectTimeoutMs);
  }

  private clearMinimizedCustomerReconnectTimeout(): void {
    if (!this.minimizedCustomerReconnectTimeout) return;

    clearTimeout(this.minimizedCustomerReconnectTimeout);
    this.minimizedCustomerReconnectTimeout = null;
  }

  private shouldWaitForMinimizedCustomerConnection(): boolean {
    return this.activeAudioCallUiService.isMinimized
      && !this.callSessionService.isMediaSessionEnding
      && !this.isHandlingMinimizedReconnectFailure
      && this.callSessionService.selectedOrder?.isCustomer === true
      && this.hasStartedActiveAudioCall()
      && !this.connectAudioService.webrtcAudioService.isConnected;
  }

  private async handleMinimizedReconnectFailed(reason: string): Promise<boolean> {
    if (!this.activeAudioCallUiService.isMinimized) return false;
    if (!this.hasActiveAudioCall()) return false;
    if (this.isHandlingMinimizedReconnectFailure) return true;

    console.warn('Stopping minimized audio call after reconnect failure', reason);
    this.isHandlingMinimizedReconnectFailure = true;
    this.clearMinimizedCustomerReconnectTimeout();
    this.connectAudioService.webrtcAudioService.stopReconnectAttempts();

    try {
      await this.stopCurrentAudioCall({
        sendToPartner: true,
      });
      return true;
    } finally {
      this.isHandlingMinimizedReconnectFailure = false;
    }
  }

  private async stopCurrentAudioCall(options: MinimizedAudioCallStopOptions): Promise<void> {
    const identity = this.callSessionService.mediaSessionIdentity;
    this.callSessionService.markMediaSessionEnding(identity);
    const isCurrent = (): boolean => this.callSessionService.mediaSessionIdentity === identity;
    const isCustomer: boolean | null | undefined = this.callSessionService.selectedOrder?.isCustomer;
    this.cancelIncomingAudioCall(this.callSessionService.selectedOrder?.id, options.sendToPartner);

    this.clearMinimizedCustomerReconnectTimeout();
    try {
      if (isCustomer === true) {
        await this.connectAudioService.stopWatchingByCustomer(options.sendToPartner);
      } else if (isCustomer === false) {
        await this.connectAudioService.stopSharingForExecutor(options.sendToPartner);
      }
    } finally {
      if (isCurrent()) {
        this.activeAudioCallUiService.clear();
        this.connectionDebug.deactivate();
        try {
          await this.activeAudioCallNativeService.deactivateForCurrentCall({finalizeAudioSession: true});
        } finally {
          if (isCurrent()) {
            this.callSessionService.clearSelectedOrder();
            this.navigationStore.clearBackUrl();
          }
        }
      }
    }
  }

  private async processReliableMinimizedAudioCallEvent(
    meta: AudioCallReliableMeta | null,
    processedMessages: Map<string, number>,
    processEvent: () => Promise<AudioCallDeliveryStatus>,
  ): Promise<void> {
    const messageId: string | null = this.normalizeReliableMessageId(meta);
    const processingKey: string | null = this.buildReliableProcessingKey(meta);
    if (!meta || !messageId || !processingKey) {
      await processEvent();
      return;
    }

    if (this.isProcessedReliableMessage(processedMessages, messageId)) {
      this.webSocketController.emitAudioCallDeliveryReceipt(meta, 'duplicate');
      return;
    }

    const activeProcessing: Promise<ReliableAudioCallProcessingResult> | null =
      this.getReliableMessageInProcessing(processingKey);
    if (activeProcessing) {
      const activeResult: ReliableAudioCallProcessingResult = await activeProcessing;
      const duplicateStatus: AudioCallDeliveryStatus = activeResult.status === 'processed'
        ? 'duplicate'
        : activeResult.status;
      this.webSocketController.emitAudioCallDeliveryReceipt(meta, duplicateStatus, activeResult.errorMessage);
      return;
    }

    const processingPromise: Promise<ReliableAudioCallProcessingResult> = (async (): Promise<ReliableAudioCallProcessingResult> => {
      let status: AudioCallDeliveryStatus = 'ignored';
      let errorMessage: string | undefined;
      let processingError: unknown;

      try {
        status = await processEvent();
        if (status === 'processed') {
          this.markReliableMessageProcessed(processedMessages, messageId);
        }
      } catch (error) {
        status = 'failed';
        errorMessage = this.resolveAudioCallDeliveryErrorMessage(error);
        processingError = error;
      } finally {
        this.unmarkReliableMessageProcessing(processingKey);
      }

      return {
        status,
        errorMessage,
        error: processingError,
      };
    })();

    this.markReliableMessageProcessing(processingKey, processingPromise);
    const processingResult: ReliableAudioCallProcessingResult = await processingPromise;
    this.webSocketController.emitAudioCallDeliveryReceipt(meta, processingResult.status, processingResult.errorMessage);

    if (processingResult.error) {
      throw processingResult.error;
    }
  }

  private cleanupProcessedReliableMessages(processedMessages: Map<string, number>): void {
    const now: number = Date.now();
    processedMessages.forEach((expiresAt: number, messageId: string): void => {
      if (expiresAt > now) return;

      processedMessages.delete(messageId);
    });
  }

  private isProcessedReliableMessage(processedMessages: Map<string, number>, messageId: string): boolean {
    this.cleanupProcessedReliableMessages(processedMessages);
    const expiresAt: number | undefined = processedMessages.get(messageId);
    if (!expiresAt) return false;
    if (expiresAt <= Date.now()) {
      processedMessages.delete(messageId);
      return false;
    }

    return true;
  }

  private markReliableMessageProcessed(processedMessages: Map<string, number>, messageId: string): void {
    this.cleanupProcessedReliableMessages(processedMessages);
    processedMessages.set(messageId, Date.now() + this.processedReliableMessageTtlMs);
  }

  private getReliableMessageInProcessing(processingKey: string): Promise<ReliableAudioCallProcessingResult> | null {
    return this.processingReliableMessages.get(processingKey) ?? null;
  }

  private markReliableMessageProcessing(
    processingKey: string,
    processingPromise: Promise<ReliableAudioCallProcessingResult>,
  ): void {
    this.processingReliableMessages.set(processingKey, processingPromise);
  }

  private unmarkReliableMessageProcessing(processingKey: string): void {
    this.processingReliableMessages.delete(processingKey);
  }

  private normalizeReliableMessageId(meta: AudioCallReliableMeta | null | undefined): string | null {
    if (typeof meta?.message_id !== 'string') return null;

    const messageId: string = meta.message_id.trim();
    return messageId.length > 0 ? messageId : null;
  }

  private buildReliableProcessingKey(meta: AudioCallReliableMeta | null | undefined): string | null {
    const messageId: string | null = this.normalizeReliableMessageId(meta);
    if (!messageId || !meta?.event) return null;

    return `${meta.event}:${messageId}`;
  }

  private resolveAudioCallDeliveryErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string' && error.trim().length > 0) {
      return error.trim();
    }

    return 'Event processing failed';
  }

  private async resolveChatIdForPartner(partnerId: number): Promise<number | null> {
    const chatRequest: ChatRequest = new ChatRequest({
      user_2_id: partnerId,
    });
    const chatData: ChatDataResponse = await firstValueFrom(this.client.chatPost(chatRequest));
    return this.normalizeOrderId(chatData?.data?.id);
  }

  private async ensureMicrophonePermission(): Promise<boolean> {
    try {
      const permissionState: PermissionState = await this.callPermissionsService.getMicrophonePermission();
      if (permissionState === 'denied') {
        await this.toastService.warning(this.translate.instant('chat.call.microphonePermissionDenied'));
        return false;
      }
      return true;
    } catch (error) {
      console.error('Microphone permission check failed', error);
      return true;
    }
  }

  private async waitForOutgoingSocketInit(cancelSignal: Subject<void>): Promise<boolean> {
    if (this.webSocketController.isSocketInitedSubject.value) return true;
    try {
      await firstValueFrom(this.webSocketController.isSocketInitedSubject.pipe(
        filter((inited: boolean): boolean => inited),
        takeUntil(cancelSignal),
        timeout({first: this.outgoingSocketInitTimeoutMs}),
      ));
      return this.webSocketController.socketConnected;
    } catch {
      return false;
    }
  }

  private scheduleIncomingAudioAccept(orderId: number): void {
    const previous = this.incomingCallAcceptOperations.get(orderId);
    if (previous?.isCurrent()) return;
    previous?.cancel();
    const selectedOrder = this.callSessionService.selectedOrder;
    const userId = this.authService.parsedToken?.userId;
    if (!userId || selectedOrder?.id !== orderId || selectedOrder.isCustomer !== false
      || selectedOrder.type !== CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL || selectedOrder.myUserId !== userId) return;
    const identity = this.callSessionService.mediaSessionIdentity;
    let attemptId: string;
    try {
      attemptId = this.callSessionService.getAttempt(orderId, userId) ?? createAudioCallAttemptId();
      if (!this.callSessionService.setAttempt(orderId, userId, attemptId)) return;
    } catch {
      this.connectionDebug.milestone('ui.accept_identity_failed', undefined, 'error');
      this.incomingAcceptFailed$.next(orderId);
      return;
    }
    const cancelSignal = new Subject<void>();
    const subscriptions = new Subscription();
    let cancelled = false;
    let requestSent = false;
    const isCurrent = (): boolean => !cancelled && this.authService.parsedToken?.userId === userId
      && this.callSessionService.mediaSessionIdentity === identity
      && this.callSessionService.selectedOrder?.id === orderId
      && this.callSessionService.selectedOrder.myUserId === userId;
    const operation: IncomingAudioAcceptOperation = {
      isCurrent,
      cancel: (finalize: boolean = true): void => {
        if (cancelled) return;
        if (isCurrent()) this.connectionDebug.milestone('ui.accept_cancelled', undefined, 'warning');
        cancelled = true;
        cancelSignal.next();
        if (finalize && requestSent) void this.finalization.finish(orderId, false, userId, attemptId);
      },
      result: Promise.resolve(),
    };
    this.incomingCallAcceptOperations.set(orderId, operation);
    subscriptions.add(this.authService.parsedToken$.subscribe(token => {
      if (token?.userId !== userId) operation.cancel();
    }));
    subscriptions.add(this.callSessionService.selectedOrderChanged$.subscribe(() => {
      if (!isCurrent()) operation.cancel();
    }));
    operation.result = this.acceptIncomingAudioCall(orderId, attemptId, userId, isCurrent, cancelSignal, (): void => { requestSent = true; })
      .catch((): void => {
        if (isCurrent()) this.connectionDebug.milestone('ui.accept_cleanup_failed', undefined, 'error');
      })
      .finally((): void => {
        subscriptions.unsubscribe();
        cancelSignal.complete();
        if (this.incomingCallAcceptOperations.get(orderId) === operation) this.incomingCallAcceptOperations.delete(orderId);
      });
  }

  private async acceptIncomingAudioCall(
    orderId: number, attemptId: string, userId: number, isCurrent: () => boolean,
    cancelSignal: Subject<void>, markRequestSent: () => void,
  ): Promise<void> {
    if (!isCurrent()) return;
    this.connectionDebug.milestone('ui.accept_started');
    const deadline = Date.now() + this.incomingAcceptDeadlineMs;
    let lastError: unknown = null;
    let requestSent = false;
    const socketReady = await this.waitForSocketInitWithTimeout(this.incomingSocketInitTimeoutMs, cancelSignal);
    if (!isCurrent()) return;
    if (!socketReady) {
      this.connectionDebug.milestone('ui.accept_socket_timeout', undefined, 'error');
      await this.handleIncomingAcceptFailure(orderId, false, isCurrent, cancelSignal);
      return;
    }

    for (let attempt: number = 1; attempt <= this.incomingAcceptRetryCount; attempt++) {
      if (!isCurrent()) return;
      const remaining = deadline - Date.now();
      if (remaining <= 0 || !this.webSocketController.socketConnected || !this.webSocketController.isSocketInitedSubject.value) break;
      try {
        requestSent = true;
        markRequestSent();
        // Unsubscribe the actual HTTP transport, not only a Promise.race observer.
        const response = await firstValueFrom(this.client.avatarOrderAccept(orderId, 0, 0,
          new AudioCallAttemptAcceptRequest({call_attempt_id: attemptId, call_actor_id: userId})).pipe(
          timeout(Math.min(this.incomingAcceptResponseTimeoutMs, remaining)), takeUntil(cancelSignal),
        ));
        if (!isCurrent()) return;
        if (response?.data?.id !== orderId || response.data.type !== CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL
          || response.data.to_id !== userId || response.data.status !== CALL_ORDER_STATUS_NUMBERS.ACCEPTED) {
          throw new Error('Unconfirmed audio call acceptance');
        }
        this.connectionDebug.milestone('ui.accept_confirmed', {attempt});
        this.watchInitialConnection(orderId);
        return;
      } catch (error) {
        if (!isCurrent()) return;
        this.connectionDebug.milestone('ui.accept_failed', {attempt}, 'warning');
        lastError = error;
        if (!this.isRecoverableIncomingAcceptError(error) || attempt === this.incomingAcceptRetryCount
          || deadline - Date.now() <= this.incomingAcceptRetryDelayMs) break;
        try {
          await firstValueFrom(timer(this.incomingAcceptRetryDelayMs).pipe(takeUntil(cancelSignal)));
        } catch {
          return;
        }
      }
    }
    if (!isCurrent()) return;
    this.connectionDebug.milestone('ui.accept_exhausted', {status: this.getCallErrorInfo(lastError).status}, 'error');
    await this.handleIncomingAcceptFailure(orderId, requestSent, isCurrent, cancelSignal,
      this.getCallErrorInfo(lastError).status === HttpStatusCode.Conflict);
  }

  private async waitForSocketInitWithTimeout(timeoutMs: number, cancelSignal: Subject<void>): Promise<boolean> {
    if (this.webSocketController.socketConnected && this.webSocketController.isSocketInitedSubject.value) return true;
    this.webSocketController.socketConnect();

    try {
      await firstValueFrom(this.webSocketController.isSocketInitedSubject.pipe(
        filter((inited: boolean): boolean => inited && this.webSocketController.socketConnected),
        timeout({first: timeoutMs}), takeUntil(cancelSignal),
      ));
      return this.webSocketController.socketConnected;
    } catch {
      return false;
    }
  }

  private async handleIncomingAcceptFailure(
    orderId: number, requestSent: boolean, isCurrent: () => boolean, cancelSignal: Subject<void>,
    ownershipDenied: boolean = false,
  ): Promise<void> {
    if (!isCurrent()) return;
    if (this.connectAudioService.webrtcAudioService.isConnected && !ownershipDenied) return;
    const userId = this.authService.parsedToken?.userId;
    const attemptId = this.callSessionService.getAttempt(orderId, userId);
    // A lost response is not proof that SQL rejected the acceptance.
    if (requestSent && !ownershipDenied) {
      try {
        const response = await firstValueFrom(this.client.avatarOrderGet(orderId).pipe(timeout(2000), takeUntil(cancelSignal)));
        if (!isCurrent()) return;
        const order = response?.data;
        if (order?.id === orderId && order.type === CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL
          && order.to_id === this.authService.parsedToken?.userId) {
          if (this.callOrderService.isOrderFinal(order.status)) {
            this.orderUpdates.updateOrder(order);
            return;
          }
        }
      } catch {
        if (!isCurrent()) return;
      }
    }
    if (!isCurrent() || (this.connectAudioService.webrtcAudioService.isConnected && !ownershipDenied)) return;
    if (attemptId && !ownershipDenied) void this.finalization.finish(orderId, false, userId, attemptId);
    // A status GET proves neither this attempt's acceptance nor ownership. Close
    // locally; the fenced HTTP finalizer, not legacy socket exit, handles cleanup.
    // Let the existing screen/native teardown run, instead of just clearing its selected order.
    this.incomingAcceptFailed$.next(orderId);
    await this.toastService.error(this.translate.instant('call.toasts.broadcastError'));
  }

  private isRecoverableIncomingAcceptError(error: unknown): boolean {
    if (error instanceof TimeoutError) return true;
    const status = this.getCallErrorInfo(error).status;
    return status !== null && [0, 500, 502, 503, 504].includes(status);
  }

  private getIncomingCallFailedResult(): IncomingCallOpenResult {
    return {
      opened: false,
      navigationCommands: null,
      orderId: null,
      shouldAcceptAudioInBackground: false,
      audioOrderResponse: null,
    };
  }

  private getCallErrorInfo(error: unknown): { status: number | null; message: string | null } {
    if (typeof error !== 'object' || error === null) {
      return { status: null, message: null };
    }

    const statusValue: unknown = Reflect.get(error, 'status');
    const status: number | null = (typeof statusValue === 'number') ? statusValue : null;

    const resultValue: unknown = Reflect.get(error, 'result');
    if (typeof resultValue !== 'object' || resultValue === null) {
      return { status, message: null };
    }

    const messageValue: unknown = Reflect.get(resultValue, 'message');
    const message: string | null = (typeof messageValue === 'string') ? messageValue : null;
    return { status, message };
  }

  private normalizeOrderId(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value) && value > 0) {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed: string = value.trim();
      if (!trimmed.length) return null;
      const parsed: number = Number(trimmed);
      if (Number.isFinite(parsed) && Number.isInteger(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return null;
  }

  private getPathFromUrl(url: string): string {
    const rawUrl: string = typeof url === 'string' ? url : '';
    const urlWithoutQuery: string = rawUrl.split('?')[0];
    const pathWithoutFragment: string = urlWithoutQuery.split('#')[0];
    return pathWithoutFragment.toLowerCase();
  }
}
